/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import CloseIcon from '@material-ui/icons/Close';
import GetApp from '@material-ui/icons/GetApp';
import { withStyles } from '@material-ui/styles';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { BASE_API, USER_TOKEN } from 'config/consts';
import LoadingProgress from 'common/components/progress/loading';
import Card from 'material-dashboard-components/Card/Card.js';
import CardHeader from 'material-dashboard-components/Card/CardHeader.js';
import CardIcon from 'material-dashboard-components/Card/CardIcon.js';
import CardFooter from 'material-dashboard-components/Card/CardFooter.js';
import GridContainer from 'material-dashboard-components/Grid/GridContainer.js';
import GridItem from 'material-dashboard-components/Grid/GridItem.js';
import styles from 'material-dashboard-assets/jss/material-dashboard-react/views/dashboardStyle.js';

const formatCurrency = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value) => {
  const n = Number(value || 0);
  return `${n.toFixed(2).replace('.', ',')}%`;
};

const formatDateOnly = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const mapStatusLabel = (status) => {
  switch (status) {
    case 'pending':
    case 'waiting_payment':
    case 'processing':
      return 'Aberto';
    case 'paid':
      return 'Pago';
    case 'expired':
      return 'Expirado';
    case 'canceled':
      return 'Cancelado';
    case 'refunded':
      return 'Estornado';
    case 'refused':
    case 'failed':
      return 'Recusado';
    case 'unpaid':
      return 'Inadimplente';
    // Estados exclusivos da Core API v5.
    case 'partial_canceled':
      return 'Estornado parcial';
    case 'chargedback':
      return 'Chargeback';
    case 'overpaid':
      return 'Pago a maior';
    case 'underpaid':
      return 'Pago a menor';
    default:
      return status || '';
  }
};

const mapStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'primary';
    case 'canceled':
    case 'refused':
    case 'failed':
    case 'unpaid':
    case 'chargedback':
    case 'underpaid':
      return 'secondary';
    default:
      return 'default';
  }
};

const mapPaymentMethod = (method) => {
  if (!method) return '';
  const normalized = method.toLowerCase();
  if (normalized === 'pix') return 'PIX';
  if (normalized.includes('credit')) return 'Cartão de crédito';
  if (normalized.includes('debit')) return 'Cartão de débito';
  if (normalized === 'boleto') return 'Boleto';
  return method;
};

const ResultsModal = ({ classes, open, onClose, activity }) => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (open && activity?.id) {
      fetchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id]);

  useEffect(() => {
    if (!open || !activity?.id) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      fetchResults({ silent: true });
    }, 5000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id]);

  const fetchResults = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/actions/${activity.id}/transactions`, {
        headers: { Authorization: token },
      });
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar resultados:', error);
      if (!silent) setTransactions([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const totalSales = transactions.length;
  const paidCount = transactions.filter(
    (t) => t.status === 'paid' || t.status === 'overpaid'
  ).length;
  const totalRevenue = transactions.reduce(
    (sum, t) => sum + (t.status === 'paid' ? Number(t.amount || 0) : 0),
    0,
  );
  const totalNet = transactions.reduce(
    (sum, t) => sum + (t.status === 'paid' ? Number(t.netValue || 0) : 0),
    0,
  );
  const totalPartner = transactions.reduce(
    (sum, t) => sum + (t.status === 'paid' ? Number(t.partnerValue || 0) : 0),
    0,
  );
  const totalStation = transactions.reduce(
    (sum, t) => sum + (t.status === 'paid' ? Number(t.stationValue || 0) : 0),
    0,
  );

  const handleDownloadXlsx = () => {
    const exportData = transactions.map((t) => ({
      Nome: t.name || '',
      CPF: t.cpf || '',
      Telefone: t.phone || '',
      'E-mail': t.email || '',
      Entidade: t.entity || '',
      Código: t.code || '',
      Produto: t.product_name || '',
      Quantidade: t.quantity || 1,
      Valor: formatCurrency(t.value),
      'Taxa de serviço': formatPercent(t.feePercentage),
      'Custo de operação': formatCurrency(t.fee),
      'Valor Líquido': formatCurrency(t.netValue),
      'Valor Parceiro': formatCurrency(t.partnerValue),
      'Valor Estação': formatCurrency(t.stationValue),
      'Forma de Pagamento': mapPaymentMethod(t.paymentMethod),
      'Data da primeira compra': formatDateOnly(t.createdAt),
      'Status da compra': mapStatusLabel(t.status),
      'É recorrente?':
        t.recurrence_type && t.recurrence_type !== 'single' ? 'Sim' : 'Não',
      Recorrência: t.recurrence || '-',
      'Última atualização do pagamento':
        t.recurrence_type && t.recurrence_type !== 'single' && t.lastPaymentUpdate
          ? formatDateTime(t.lastPaymentUpdate)
          : '',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resultados');

    ws['!cols'] = [
      { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 20 },
      { wch: 10 }, { wch: 30 }, { wch: 10 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 20 },
      { wch: 18 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 22 },
    ];

    const safeName = (activity?.name || 'Atividade')
      .replace(/[^a-z0-9\- ]/gi, '')
      .replace(/\s+/g, '_');
    const fileName = `Resultados_${safeName}_${new Date()
      .toLocaleDateString('pt-BR')
      .replace(/\//g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Resultados - {activity?.name || 'Atividade'}</Typography>
          <IconButton onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <LoadingProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" style={{ marginBottom: 16 }}>
              Resultados gerais
            </Typography>
            <GridContainer>
              <GridItem xs={12} sm={6} md={4}>
                <Card style={{ position: 'relative' }} className="active-actions">
                  <CardHeader color="warning" stats icon>
                    <CardIcon color="white">
                      <Icon color="primary">shopping_cart</Icon>
                    </CardIcon>
                    <p className={classes.cardCategory}>Total de vendas</p>
                    <h3 className={classes.cardTitle}>{totalSales}</h3>
                  </CardHeader>
                  <CardFooter stats style={{ position: 'absolute', bottom: 5, width: '90%' }}>
                    <div className={classes.stats} style={{ cursor: 'pointer' }}>
                      <Tooltip
                        style={{ marginLeft: 10 }}
                        title="Quantidade de links de pagamento gerados para esta atividade."
                        placement="top-start">
                        <Icon color="default">help</Icon>
                      </Tooltip>
                    </div>
                  </CardFooter>
                </Card>
              </GridItem>

              <GridItem xs={12} sm={6} md={4}>
                <Card style={{ position: 'relative' }} className="active-actions">
                  <CardHeader color="warning" stats icon>
                    <CardIcon color="white">
                      <Icon color="primary">check_circle</Icon>
                    </CardIcon>
                    <p className={classes.cardCategory}>Vendas pagas</p>
                    <h3 className={classes.cardTitle}>{paidCount}</h3>
                  </CardHeader>
                  <CardFooter stats style={{ position: 'absolute', bottom: 5, width: '90%' }}>
                    <div className={classes.stats} style={{ cursor: 'pointer' }}>
                      <Tooltip
                        style={{ marginLeft: 10 }}
                        title="Total de links cujo pagamento foi confirmado."
                        placement="top-start">
                        <Icon color="default">help</Icon>
                      </Tooltip>
                    </div>
                  </CardFooter>
                </Card>
              </GridItem>

              <GridItem xs={12} sm={6} md={4}>
                <Card style={{ position: 'relative' }} className="active-actions">
                  <CardHeader color="warning" stats icon>
                    <CardIcon color="white">
                      <Icon color="primary">attach_money</Icon>
                    </CardIcon>
                    <p className={classes.cardCategory}>Receita bruta</p>
                    <h3 className={classes.cardTitle}>{formatCurrency(totalRevenue)}</h3>
                  </CardHeader>
                  <CardFooter stats style={{ position: 'absolute', bottom: 5, width: '90%' }}>
                    <div className={classes.stats} style={{ cursor: 'pointer' }}>
                      <Tooltip
                        style={{ marginLeft: 10 }}
                        title="Soma dos valores cobrados nas vendas confirmadas (antes de taxas)."
                        placement="top-start">
                        <Icon color="default">help</Icon>
                      </Tooltip>
                    </div>
                  </CardFooter>
                </Card>
              </GridItem>

              <GridItem xs={12} sm={6} md={4}>
                <Card style={{ position: 'relative' }} className="active-actions">
                  <CardHeader color="warning" stats icon>
                    <CardIcon color="white">
                      <Icon color="primary">account_balance_wallet</Icon>
                    </CardIcon>
                    <p className={classes.cardCategory}>Receita líquida</p>
                    <h3 className={classes.cardTitle}>{formatCurrency(totalNet)}</h3>
                  </CardHeader>
                  <CardFooter stats style={{ position: 'absolute', bottom: 5, width: '90%' }}>
                    <div className={classes.stats} style={{ cursor: 'pointer' }}>
                      <Tooltip
                        style={{ marginLeft: 10 }}
                        title="Soma dos valores líquidos (bruto menos taxa de administração) das vendas confirmadas."
                        placement="top-start">
                        <Icon color="default">help</Icon>
                      </Tooltip>
                    </div>
                  </CardFooter>
                </Card>
              </GridItem>

              <GridItem xs={12} sm={6} md={4}>
                <Card style={{ position: 'relative' }} className="active-actions">
                  <CardHeader color="warning" stats icon>
                    <CardIcon color="white">
                      <Icon color="primary">people_alt</Icon>
                    </CardIcon>
                    <p className={classes.cardCategory}>Valor parceiros</p>
                    <h3 className={classes.cardTitle}>{formatCurrency(totalPartner)}</h3>
                  </CardHeader>
                  <CardFooter stats style={{ position: 'absolute', bottom: 5, width: '90%' }}>
                    <div className={classes.stats} style={{ cursor: 'pointer' }}>
                      <Tooltip
                        style={{ marginLeft: 10 }}
                        title="Valor repassado aos parceiros via split de pagamentos confirmados."
                        placement="top-start">
                        <Icon color="default">help</Icon>
                      </Tooltip>
                    </div>
                  </CardFooter>
                </Card>
              </GridItem>

              <GridItem xs={12} sm={6} md={4}>
                <Card style={{ position: 'relative' }} className="active-actions">
                  <CardHeader color="warning" stats icon>
                    <CardIcon color="white">
                      <Icon color="primary">business</Icon>
                    </CardIcon>
                    <p className={classes.cardCategory}>Valor estação</p>
                    <h3 className={classes.cardTitle}>{formatCurrency(totalStation)}</h3>
                  </CardHeader>
                  <CardFooter stats style={{ position: 'absolute', bottom: 5, width: '90%' }}>
                    <div className={classes.stats} style={{ cursor: 'pointer' }}>
                      <Tooltip
                        style={{ marginLeft: 10 }}
                        title="Valor retido pela estação após taxas e split com parceiros."
                        placement="top-start">
                        <Icon color="default">help</Icon>
                      </Tooltip>
                    </div>
                  </CardFooter>
                </Card>
              </GridItem>
            </GridContainer>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              mb={1}>
              <Typography variant="subtitle1">Transações</Typography>
              <Tooltip title="Baixar tabela em Excel">
                <IconButton
                  onClick={handleDownloadXlsx}
                  disabled={!transactions.length}
                  aria-label="Baixar Excel">
                  <GetApp color="primary" />
                </IconButton>
              </Tooltip>
            </Box>

            <TableContainer component={Paper} style={{ overflowX: 'auto' }}>
              <Table size="small" style={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>CPF</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Taxa</TableCell>
                    <TableCell align="right">Líquido</TableCell>
                    <TableCell align="right">Parceiro</TableCell>
                    <TableCell align="right">Estação</TableCell>
                    <TableCell>Forma de pagamento</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recorrência</TableCell>
                    <TableCell>Última atualização</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={15} align="center">
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ padding: '24px 0' }}>
                          Nenhuma transação encontrada
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t) => {
                      const isRecurring =
                        t.recurrence_type && t.recurrence_type !== 'single';
                      return (
                        <TableRow key={t.id}>
                          <TableCell>{t.name || '-'}</TableCell>
                          <TableCell>{t.cpf || '-'}</TableCell>
                          <TableCell>{t.email || '-'}</TableCell>
                          <TableCell>{t.code || '-'}</TableCell>
                          <TableCell>{t.product_name || '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(t.value)}</TableCell>
                          <TableCell align="right">{formatCurrency(t.fee)}</TableCell>
                          <TableCell align="right">{formatCurrency(t.netValue)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(t.partnerValue)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(t.stationValue)}
                          </TableCell>
                          <TableCell>{mapPaymentMethod(t.paymentMethod)}</TableCell>
                          <TableCell>{formatDateOnly(t.createdAt)}</TableCell>
                          <TableCell>
                            <Chip
                              label={mapStatusLabel(t.status)}
                              color={mapStatusColor(t.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{isRecurring ? t.recurrence : 'Não'}</TableCell>
                          <TableCell>
                            {isRecurring && t.lastPaymentUpdate
                              ? formatDateTime(t.lastPaymentUpdate)
                              : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

ResultsModal.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activity: PropTypes.object,
};

export default withStyles(styles)(ResultsModal);
